import { useEffect, useMemo, useState } from 'react';
import { AUDIT_KINDS, type AuditKind } from '@shared';
import { apiGet, ApiError } from '../../lib/api';

// Server-side AuditEntry shape (after route serialisation).
interface AuditRow {
  id: string;
  kind: AuditKind;
  actor_id: string | null;
  actor_name: string;
  actor_kind: 'staff' | 'client' | 'system';
  what: string;
  resource: string | null;
  resource_id: string | null;
  at: string;
  d: string;
  t: string;
}

const KIND_LABEL: Record<AuditKind, string> = {
  submit: 'Submit',
  proposal: 'Proposal',
  kyc: 'KYC',
  user: 'User',
  finance: 'Finance',
  doc: 'Document',
  system: 'System',
  auth: 'Auth',
  cms: 'CMS',
  engagement: 'Engagement',
  message: 'Message',
  client: 'Client',
};

const KIND_TONE: Partial<Record<AuditKind, string>> = {
  proposal: 'accent',
  kyc: 'amber',
  user: 'amber',
  finance: 'green',
  doc: 'accent',
  system: 'neutral',
  auth: 'neutral',
  cms: 'neutral',
  engagement: 'accent',
  submit: 'green',
  message: 'neutral',
};

export function AdminAudit() {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [kind, setKind] = useState<'all' | AuditKind>('all');
  const [actor, setActor] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (kind !== 'all') params.set('kind', kind);
    if (actor.trim()) params.set('actor_id', actor.trim());
    params.set('limit', '200');
    apiGet<{ entries: AuditRow[] }>(`/api/audit?${params.toString()}`)
      .then((r) => setRows(r.entries))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message))
      .finally(() => setLoading(false));
  }, [kind, actor]);

  const counts = useMemo(() => {
    let staff = 0, client = 0, system = 0;
    for (const r of rows ?? []) {
      if (r.actor_kind === 'staff') staff++;
      else if (r.actor_kind === 'client') client++;
      else if (r.actor_kind === 'system') system++;
    }
    return { total: rows?.length ?? 0, staff, client, system };
  }, [rows]);

  return (
    <>
      <div>
        <h1 className="portal-h1">Audit log</h1>
        <p className="portal-sub">
          Immutable record of every state change, KYC review, CMS edit, role change and document
          interaction. Append-only by design — entries cannot be edited or deleted (REQ-007).
        </p>
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Showing</div>
          <div className="val">{counts.total}</div>
          <div className="delta">Most-recent first</div>
        </div>
        <div className="card">
          <div className="lbl">Staff actions</div>
          <div className="val">{counts.staff}</div>
          <div className="delta">From operator portal</div>
        </div>
        <div className="card">
          <div className="lbl">Client actions</div>
          <div className="val">{counts.client}</div>
          <div className="delta">From client portal</div>
        </div>
        <div className="card">
          <div className="lbl">System</div>
          <div className="val">{counts.system}</div>
          <div className="delta">Automated</div>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        <button className={kind === 'all' ? 'on' : ''} onClick={() => setKind('all')}>All</button>
        {AUDIT_KINDS.map((k) => (
          <button key={k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>
            {KIND_LABEL[k]}
          </button>
        ))}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <label className="prop-lbl" htmlFor="audit-actor">Filter by actor (user id)</label>
        <input
          id="audit-actor"
          className="prop-input"
          placeholder="e.g. u-001"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
        />
        <div className="prop-hint">Leave blank for all actors. Filter applies on blur/keystroke.</div>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="card flush" style={{ marginTop: 16 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>When</th>
              <th>Kind</th>
              <th>Actor</th>
              <th>What</th>
              <th>Resource</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {!loading && rows !== null && rows.length === 0 && (
              <tr>
                <td colSpan={5} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No audit entries match this filter.
                </td>
              </tr>
            )}
            {(rows ?? []).map((r) => (
              <tr key={r.id}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-mute)', whiteSpace: 'nowrap' }}>
                  {r.d} {r.t}
                </td>
                <td>
                  <span className={`chip ${KIND_TONE[r.kind] ?? ''}`}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                    {KIND_LABEL[r.kind]}
                  </span>
                </td>
                <td style={{ fontSize: 13 }}>
                  <div>{r.actor_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                    {r.actor_kind}{r.actor_id ? ` · ${r.actor_id}` : ''}
                  </div>
                </td>
                <td style={{ fontSize: 13, color: 'var(--fg-soft)' }}>{r.what}</td>
                <td style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                  {r.resource ? `${r.resource}${r.resource_id ? ` / ${r.resource_id}` : ''}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
